import React from 'react';
import { IconChevron } from '../../assets/icons/IconChevron';
import type { LinkButtonProps, LinkButtonSize, LinkButtonVariant, LinkButtonWeight } from './types';

const SIZE_CLASS: Record<LinkButtonSize, string> = {
    xs: 'text-xs',
    s: 'text-s',
    m: 'text-body',
};

// The underline takes the text colour, so the tertiary hover darkens both together.
const VARIANT_CLASS: Record<LinkButtonVariant, string> = {
    tertiary: 'underline decoration-1 underline-offset-2 text-content-subtle hover:text-content active:text-content',
    navigation: 'decoration-1 underline-offset-2 text-accent-300 hover:underline active:underline',
};

const DISABLED_CLASS: Record<LinkButtonVariant, string> = {
    tertiary: 'underline decoration-1 underline-offset-2 text-content-tertiary cursor-not-allowed',
    navigation: 'text-content-tertiary cursor-not-allowed',
};

const WEIGHT_CLASS: Record<LinkButtonWeight, string> = {
    regular: 'font-normal',
    medium: 'font-medium',
};

const DEFAULT_SIZE: Record<LinkButtonVariant, LinkButtonSize> = {
    tertiary: 's',
    navigation: 'm',
};

/**
 * The app's text link, regular weight unless `weight` says otherwise, in two roles:
 * - `tertiary` (default, grey, always underlined, darker on hover) — the third-level button beside
 *   primary and secondary: an action in context, such as "Ignorer / repousser" or "Réessayer".
 * - `navigation` (purple, underlined on hover) — moves elsewhere or leaves the page: the header's
 *   back link (`chevron="start"`), "Rafraîchir", `SeeAllLink` (`chevron="end"`).
 * Not `Button`: that one is for boxed primary/secondary actions. The chevron is an SVG, so the
 * text underline never runs under it.
 */
export const LinkButton: React.FC<LinkButtonProps> = ({
    size,
    variant = 'tertiary',
    chevron,
    weight = 'regular',
    disabled,
    className = '',
    children,
    ...props
}) => (
    <button
        type="button"
        disabled={disabled}
        className={
            `inline-flex items-center gap-xs rounded-sm ${WEIGHT_CLASS[weight]} transition-colors touch-target ` +
            `focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring ` +
            `${SIZE_CLASS[size ?? DEFAULT_SIZE[variant]]} ` +
            (disabled ? DISABLED_CLASS[variant] : `${VARIANT_CLASS[variant]} cursor-pointer`) +
            ` ${className}`
        }
        {...props}
    >
        {/* Centred on the line box, the chevron reads high against lowercase text; 1px down aligns it. */}
        {chevron === 'start' &&<IconChevron className="shrink-0 rotate-180 translate-y-px" data-testid="link-button-chevron-start" />}
        {children}
        {chevron === 'end' && <IconChevron className="shrink-0 translate-y-px" data-testid="link-button-chevron-end" />}
    </button>
);
